// frontend/src/components/payments/SubscriptionCancellation.js
import React, { useState } from 'react';
import { 
  AlertTriangle, 
  Calendar, 
  CheckCircle, 
  XCircle, 
  Loader,
  Info,
  Shield,
  Zap,
  Building,
  Crown,
  User
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

const SubscriptionCancellation = ({ subscription, onCancelled, planInfo }) => {
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCancelSubscription = async () => {
    if (!cancellationReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }

    setLoading(true);
    try {
      await api.cancelSubscription({
        reason: cancellationReason,
        feedback: cancellationReason
      });
      
      setShowCancellationModal(false);
      setCancellationReason('');
      onCancelled();
      toast.success('Subscription cancelled successfully');
    } catch (error) {
      toast.error('Failed to cancel subscription');
      console.error('Cancellation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAccessEndDate = () => {
    if (!subscription?.current_period_end) return 'Immediately';
    return new Date(subscription.current_period_end).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getPlanIcon = () => {
    const Icon = planInfo.icon;
    // Tier colour is metadata, not a primary action — the design system
    // allows one accent per screen, and each plan already has a distinct
    // Icon shape, so a single `info` tone for all tiers is sufficient
    // (matches the precedent in SubscriptionSelection.js / StripeComponents.js).
    return <Icon className="w-8 h-8 text-info" />;
  };

  if (!subscription) {
    return (
      <div className="text-center py-12">
        <Info className="w-12 h-12 text-ink-muted mx-auto mb-4" />
        <h3 className="text-lg font-medium text-ink-soft mb-2">No Active Subscription</h3>
        <p className="text-ink-muted">
          You don't have an active subscription to manage.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Subscription Info */}
      <div className="bg-info-tint rounded-lg p-6 border border-info-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            {getPlanIcon()}
            <div className="ml-4">
              <h2 className="text-xl font-semibold text-ink-soft">{planInfo.name} Plan</h2>
              <p className="text-sm text-ink-muted">${planInfo.price}/month</p>
            </div>
          </div>
          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            subscription.status === 'active'
              ? 'bg-success-tint text-success'
              : 'bg-danger-tint text-danger'
          }`}>
            {subscription.status === 'active' ? (
              <CheckCircle className="w-4 h-4 mr-1" />
            ) : (
              <XCircle className="w-4 h-4 mr-1" />
            )}
            {subscription.status}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-surface-card rounded-lg p-4 border">
            <div className="text-sm text-ink-muted mb-1">Next Billing Date</div>
            <div className="text-lg font-medium text-ink-soft">
              {subscription.current_period_end ? 
                new Date(subscription.current_period_end).toLocaleDateString() : 
                'N/A'
              }
            </div>
          </div>
          
          <div className="bg-surface-card rounded-lg p-4 border">
            <div className="text-sm text-ink-muted mb-1">Billing Cycle</div>
            <div className="text-lg font-medium text-ink-soft">
              {subscription.billing_interval || 'monthly'}
            </div>
          </div>
        </div>
      </div>

      {/* Cancellation Section */}
      <div className="bg-surface-card border border-border-default rounded-lg p-6">
        <div className="flex items-start">
          <AlertTriangle className="w-6 h-6 text-danger mt-1 mr-3" />
          <div className="flex-1">
            <h3 className="text-lg font-medium text-ink-soft mb-2">Cancel Subscription</h3>
            <p className="text-ink-muted mb-4">
              You can cancel your subscription at any time. You'll continue to have access to all features until the end of your current billing period.
            </p>
            
            <div className="bg-warning-tint border border-warning-border rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <Info className="w-5 h-5 text-warning mt-0.5 mr-3" />
                <div className="text-sm text-warning">
                  <div className="font-medium mb-1">What happens when you cancel:</div>
                  <ul className="list-disc list-inside space-y-1">
                    <li>You'll keep access until <strong>{getAccessEndDate()}</strong></li>
                    <li>No further charges will be made</li>
                    <li>You can reactivate anytime</li>
                    <li>Your data will be preserved</li>
                  </ul>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowCancellationModal(true)}
              className="bg-danger text-white px-4 py-2 rounded-lg hover:bg-danger/80 flex items-center"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Cancel Subscription
            </button>
          </div>
        </div>
      </div>

      {/* Cancellation Modal */}
      {showCancellationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface-card rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-danger mr-3" />
              <h3 className="text-lg font-medium text-ink-soft">Cancel Subscription</h3>
            </div>
            
            <p className="text-ink-muted mb-4">
              Are you sure you want to cancel your subscription? You'll continue to have access until <strong>{getAccessEndDate()}</strong>.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-ink-soft mb-2">
                Reason for cancellation (optional)
              </label>
              <textarea
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Help us improve by telling us why you're cancelling..."
                className="w-full border border-border-strong rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
                rows={3}
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleCancelSubscription}
                disabled={loading}
                className="flex-1 bg-danger text-white py-2 px-4 rounded-lg hover:bg-danger/80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <Loader className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <XCircle className="w-4 h-4 mr-2" />
                )}
                {loading ? 'Cancelling...' : 'Yes, Cancel Subscription'}
              </button>
              <button
                onClick={() => setShowCancellationModal(false)}
                className="px-4 py-2 border border-border-default rounded-lg hover:bg-surface-page"
              >
                Keep Subscription
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionCancellation;
